/**
 * Placement geometry for the math keypad while a table cell is being edited.
 *
 * The keypad is a fixed-size panel (523×221) portaled to `document.body` and
 * anchored to the table toolbar, which itself sits below (or, when there is no
 * room, above) the focused cell's row. Stacking it blindly under the toolbar and
 * then clamping the result into the viewport is what put the keypad *on top of
 * the table*: every cell it covers becomes unclickable, so a fraction underneath
 * it cannot be reached until the keypad is dismissed.
 *
 * This module picks between four candidate positions — below / above the focused
 * row, or beside the table (right, then left) — and scores them so that:
 *   1. a position that covers nothing wins,
 *   2. otherwise one that at least keeps the *focused cell* clear wins,
 *   3. the familiar below → above → right → left order breaks ties.
 * Everything is in viewport (client) coordinates, so zoom/pan are already baked
 * into the rects the caller passes in.
 */
/** Viewport margin kept clear on every edge. */
export var KEYPAD_MARGIN = 10;
/** Gap between the toolbar and the keypad when they are stacked. */
export var KEYPAD_STACK_GAP = 5;
var clamp = function (v, min, max) { return Math.max(min, Math.min(v, max)); };
var overlaps = function (a, b) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
};
var rectAt = function (top, left, width, height) { return ({
    top: top,
    left: left,
    bottom: top + height,
    right: left + width,
}); };
export function computeKeypadPlacement(input) {
    var cell = input.cell, table = input.table, toolbarHeight = input.toolbarHeight, toolbarWidth = input.toolbarWidth, toolbarLeft = input.toolbarLeft, toolbarTopIfBelow = input.toolbarTopIfBelow, toolbarTopIfAbove = input.toolbarTopIfAbove, kw = input.keypadWidth, kh = input.keypadHeight, viewport = input.viewport, headerBottom = input.headerBottom, sidebarRight = input.sidebarRight, previousSide = input.previousSide;
    var minLeft = Math.max(sidebarRight + KEYPAD_MARGIN, KEYPAD_MARGIN);
    var maxLeft = viewport.width - kw - KEYPAD_MARGIN;
    var minTop = headerBottom + KEYPAD_MARGIN;
    var maxTop = viewport.height - kh - KEYPAD_MARGIN;
    // Left edge used by the stacked (below/above) placements — the keypad lines up
    // with the toolbar, which is aligned to the focused cell's column.
    var stackedLeft = clamp(toolbarLeft, minLeft, maxLeft);
    // The toolbar can only flip above the cell when it clears the header.
    var canFlipToolbarAbove = toolbarTopIfAbove >= minTop;
    // Side placements leave the toolbar to its own viewport rule: below the cell
    // unless it would run off the bottom.
    var toolbarOverflowsBelow = toolbarTopIfBelow + toolbarHeight > viewport.height - KEYPAD_MARGIN;
    var sideToolbarAbove = toolbarOverflowsBelow && canFlipToolbarAbove;
    var toolbarRectFor = function (above) {
        return rectAt(above ? toolbarTopIfAbove : toolbarTopIfBelow, toolbarLeft, toolbarWidth, toolbarHeight);
    };
    var candidates = [];
    // 1 & 2 — stacked with the toolbar, below then above the focused row.
    candidates.push({
        side: 'below',
        toolbarAboveCell: false,
        top: clamp(toolbarTopIfBelow + toolbarHeight + KEYPAD_STACK_GAP, minTop, maxTop),
        left: stackedLeft,
    });
    if (canFlipToolbarAbove) {
        candidates.push({
            side: 'above',
            toolbarAboveCell: true,
            top: clamp(toolbarTopIfAbove - KEYPAD_STACK_GAP - kh, minTop, maxTop),
            left: stackedLeft,
        });
    }
    // 3 & 4 — beside the table, vertically centred on the focused cell and moved
    // clear of the toolbar if it lands on it.
    var sideTop = function (toolbarAbove, left) {
        var centred = clamp(cell.top + (cell.bottom - cell.top) / 2 - kh / 2, minTop, maxTop);
        var tb = toolbarRectFor(toolbarAbove);
        if (!overlaps(rectAt(centred, left, kw, kh), tb))
            return centred;
        for (var _i = 0, _a = [tb.top - KEYPAD_STACK_GAP - kh, tb.bottom + KEYPAD_STACK_GAP]; _i < _a.length; _i++) {
            var alt = _a[_i];
            if (alt >= minTop && alt <= maxTop && !overlaps(rectAt(alt, left, kw, kh), tb))
                return alt;
        }
        return centred;
    };
    // Both side candidates are bounded on both ends: a table dragged mostly off
    // the left of the canvas can have table.right behind the sidebar, and one
    // dragged off the right can have table.left past the viewport edge.
    var rightLeft = table.right + KEYPAD_MARGIN;
    if (rightLeft <= maxLeft && rightLeft >= minLeft) {
        candidates.push({
            side: 'right',
            toolbarAboveCell: sideToolbarAbove,
            top: sideTop(sideToolbarAbove, rightLeft),
            left: rightLeft,
        });
    }
    var leftLeft = table.left - KEYPAD_MARGIN - kw;
    if (leftLeft >= minLeft && leftLeft <= maxLeft) {
        candidates.push({
            side: 'left',
            toolbarAboveCell: sideToolbarAbove,
            top: sideTop(sideToolbarAbove, leftLeft),
            left: leftLeft,
        });
    }
    // Score: covering the focused cell is worst, covering any other part of the
    // table is next, and sitting on the toolbar is disqualifying on its own.
    var score = function (c) {
        var rect = rectAt(c.top, c.left, kw, kh);
        var s = 0;
        if (overlaps(rect, table))
            s += 1;
        if (overlaps(rect, cell))
            s += 2;
        if (overlaps(rect, toolbarRectFor(c.toolbarAboveCell)))
            s += 4;
        return s;
    };
    var best = candidates[0];
    var bestScore = score(best);
    for (var i = 1; i < candidates.length; i++) {
        var s = score(candidates[i]);
        if (s < bestScore) {
            best = candidates[i];
            bestScore = s;
        }
    }
    // Hysteresis: stay on the previous side while it is no worse than the winner.
    if (previousSide && best.side !== previousSide) {
        var previous = candidates.find(function (c) { return c.side === previousSide; });
        if (previous && score(previous) <= bestScore)
            return previous;
    }
    return best;
}
